import { useCallback, useEffect, useRef, type RefObject } from "react";
import { useApp } from "../state/store";
import { contentSize, computeFitScale, buildLayout } from "../domain/layout";
import { NAVBAR_HEIGHT } from "../domain/insets";

export interface UsePanZoomArgs {
  hostRef: RefObject<HTMLDivElement>;
}

/**
 * Wires mouse drag, wheel zoom and pinch zoom to the store actions.
 * The PreviewCanvas owns this hook; the Navbar uses store actions
 * directly for the zoom buttons.
 */
export const usePanZoom = ({ hostRef }: UsePanZoomArgs) => {
  const dragRef = useRef<{ lastX: number; lastY: number } | null>(null);
  const pinchRef = useRef<{ dist: number; scale: number; midX: number; midY: number } | null>(null);

  const fitScale = useCallback((): number => {
    const s = useApp.getState();
    if (!s.source) return 1;
    const layout = buildLayout(s.paper, s.margins, s.sign, s.tile);
    const { w, h } = contentSize(layout, s.view, s.sign);
    const visibleH = Math.max(1, s.hostSize.h - NAVBAR_HEIGHT);
    return computeFitScale(s.hostSize.w, visibleH, w, h);
  }, []);

  // Mouse drag handler (returned, bound by host element via React).
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const { source } = useApp.getState();
    if (!source) return;
    dragRef.current = { lastX: e.clientX, lastY: e.clientY };
    e.preventDefault();
  }, []);

  // Mouse move / up tracked globally so a fast drag-out still pans cleanly.
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const { zoom, pan, setPan, setZoom } = useApp.getState();
      if (zoom === "fit") setZoom(fitScale());
      setPan({
        x: pan.x + (e.clientX - dragRef.current.lastX),
        y: pan.y + (e.clientY - dragRef.current.lastY),
      });
      dragRef.current = { lastX: e.clientX, lastY: e.clientY };
    };
    const onMouseUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [fitScale]);

  // Wheel + touch listeners need non-passive registration (React's onWheel cannot preventDefault).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const onWheel = (e: WheelEvent) => {
      const { source, zoomAt } = useApp.getState();
      if (!source) return;
      e.preventDefault();
      const rect = host.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      zoomAt(Math.exp(-e.deltaY * 0.0015), mx, my);
    };

    const onTouchStart = (e: TouchEvent) => {
      const { source } = useApp.getState();
      if (!source) return;
      if (e.touches.length === 1) {
        const t = e.touches[0]!;
        dragRef.current = { lastX: t.clientX, lastY: t.clientY };
      } else if (e.touches.length === 2) {
        const [a, b] = [e.touches[0]!, e.touches[1]!];
        const fit = fitScale();
        const { zoom } = useApp.getState();
        pinchRef.current = {
          dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
          scale: zoom === "fit" ? fit : zoom,
          midX: (a.clientX + b.clientX) / 2,
          midY: (a.clientY + b.clientY) / 2,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const { source, zoomAt } = useApp.getState();
      if (!source) return;
      e.preventDefault();
      if (e.touches.length === 1 && dragRef.current) {
        const t = e.touches[0]!;
        const { zoom, pan, setPan, setZoom } = useApp.getState();
        if (zoom === "fit") setZoom(fitScale());
        setPan({
          x: pan.x + (t.clientX - dragRef.current.lastX),
          y: pan.y + (t.clientY - dragRef.current.lastY),
        });
        dragRef.current = { lastX: t.clientX, lastY: t.clientY };
      } else if (e.touches.length === 2 && pinchRef.current) {
        const [a, b] = [e.touches[0]!, e.touches[1]!];
        const d = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const { zoom } = useApp.getState();
        const cur = zoom === "fit" ? fitScale() : zoom;
        const factor = (d / pinchRef.current.dist) / (cur / pinchRef.current.scale);
        const rect = host.getBoundingClientRect();
        zoomAt(factor, pinchRef.current.midX - rect.left, pinchRef.current.midY - rect.top);
      }
    };

    const onTouchEnd = () => {
      dragRef.current = null;
      pinchRef.current = null;
    };

    host.addEventListener("wheel", onWheel, { passive: false });
    host.addEventListener("touchstart", onTouchStart, { passive: false });
    host.addEventListener("touchmove", onTouchMove, { passive: false });
    host.addEventListener("touchend", onTouchEnd);
    return () => {
      host.removeEventListener("wheel", onWheel);
      host.removeEventListener("touchstart", onTouchStart);
      host.removeEventListener("touchmove", onTouchMove);
      host.removeEventListener("touchend", onTouchEnd);
    };
  }, [hostRef, fitScale]);

  return { onMouseDown };
};
